import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { addTask, updateTask } from '../store/taskSlice';
import type { NewTask } from '../api/api';

// Helper function to convert UTC ISO string to local datetime-local format
const toLocalDateTimeString = (isoString: string) => {
  const date = new Date(isoString);
  return new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
    .toISOString()
    .slice(0, 16);
};

const TaskForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const tasks = useAppSelector(state => state.tasks.tasks);
  const categories = useAppSelector(state => state.categories.categories);
  const subcategories = useAppSelector(state => state.categories.subcategories);
  
  const [taskName, setTaskName] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');  
  const [isNameTouched, setIsNameTouched] = useState(false);
  const lastAutoNameRef = React.useRef('');
  const [completeExisting, setCompleteExisting] = useState(false);
  const [startDate, setStartDate] = useState<string>(() => {
    if (!id) {
      const now = new Date();
      // Convert to local ISO string by subtracting timezone offset
      const localISOTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
        .toISOString()
        .slice(0, 16);
      return localISOTime;
    }
    return '';
  });
  const [endDate, setEndDate] = useState<string>('');

  useEffect(() => {
    if (id) {
      const task = tasks.find((t) => t.id === parseInt(id));
      if (task) {
        setTaskName(task.name);
        setCategory(task.category);
        setSubcategory(task.subcategory);
        setStartDate(toLocalDateTimeString(task.startDate));
        setEndDate(task.endDate ? toLocalDateTimeString(task.endDate) : '');
        // Determine if the loaded name is an auto-generated name
        const gen = `${task.category}-${task.subcategory}`.toLowerCase().replace(/\s+/g, '-');
        lastAutoNameRef.current = gen;
        setIsNameTouched(task.name !== gen);
      }
    }
  }, [id, tasks]);    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const taskData: NewTask = {
      name: taskName,
      category,
      subcategory,
      startDate: startDate, // Send local datetime as-is (format: "2025-11-02T14:30")
      endDate: endDate || null,
    };

    try {
      // If adding new task, either pause or finish all active tasks depending on checkbox
      if (!id) {
        const activeTasks = tasks.filter(task => !task.endDate);
        if (activeTasks.length > 0) {
          const now = new Date();
          const localDateTime = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
            .toISOString()
            .slice(0, 19);

          if (completeExisting) {
            // Pause all active tasks: set endDate and set isPaused flag
            for (const task of activeTasks) {
              await dispatch(updateTask({
                ...task,
                endDate: localDateTime,
                isPaused: 'T'
              })).unwrap();
            }
          } else {
            // Finish all active tasks: set endDate and clear isPaused
            for (const task of activeTasks) {
              await dispatch(updateTask({
                ...task,
                endDate: localDateTime,
                isPaused: null
              })).unwrap();
            }
          }
        }
      }
      
      if (id) {
        // When editing, preserve the isPaused flag from the original task
        const originalTask = tasks.find((t) => t.id === parseInt(id));
        await dispatch(updateTask({ 
          ...taskData, 
          id: parseInt(id),
          isPaused: originalTask?.isPaused
        })).unwrap();
      } else {
        await dispatch(addTask(taskData)).unwrap();
      }
      navigate('/');
    } catch (error) {
      // Error saving task
    }
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ my: { xs: 0, sm: 4 } }}>
        <Typography variant="h4" component="h1" sx={{ textAlign: 'center', pt: { xs: 1, sm: 0 }, mb: { xs: 1, sm: 2 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          {id ? 'Edit Task' : 'Add New Task'}
        </Typography>
        <Paper sx={{ p: { xs: 1, sm: 3 } }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select<string>
                  value={category}
                  onChange={(e) => {
                    if (e && e.target) {
                      const target = e.target as { value: string };
                      setCategory(target.value);
                      // reset subcategory when category changes
                      setSubcategory('');
                      // do not auto-generate name yet until subcategory selected
                      lastAutoNameRef.current = '';
                      // if user hasn't touched name, clear it so it will be generated later
                      if (!isNameTouched) setTaskName('');
                    }
                  }}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth required disabled={!category}>
                <InputLabel>Subcategory</InputLabel>
                <Select<string>
                  value={subcategory}
                  onChange={(e) => {
                    if (e && e.target) {
                      const target = e.target as { value: string };
                      const newSub = target.value;
                      setSubcategory(newSub);

                      // generate name when both category and subcategory are present
                      if (category && newSub) {
                        const gen = `${category}-${newSub}`
                          .toLowerCase()
                          .replace(/\s+/g, '-');

                        // update name if user hasn't provided a custom name
                        if (!isNameTouched || taskName === '' || taskName === lastAutoNameRef.current) {
                          setTaskName(gen);
                          setIsNameTouched(false);
                          lastAutoNameRef.current = gen;
                        }
                      }
                    }
                  }}
                  label="Subcategory"
                >
                  {subcategories[category]?.map((sub) => (
                    <MenuItem key={sub} value={sub}>
                      {sub}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Task Name"
                value={taskName}
                onChange={(e) => {
                  setTaskName((e.target as HTMLInputElement).value);
                  setIsNameTouched(true);
                }}
                fullWidth
                required
              />

              <TextField
                label="Start Date"
                type="datetime-local"
                value={startDate}
                onChange={(e) => setStartDate((e.target as HTMLInputElement).value)}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="End Date"
                type="datetime-local"
                value={endDate}
                onChange={(e) => setEndDate((e.target as HTMLInputElement).value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              {!id && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={completeExisting}
                      onChange={(e) => setCompleteExisting(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Pause existing active tasks"
                />
              )}

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/')}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {id ? 'Update Task' : 'Add Task'}
                </Button>
              </Box>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default TaskForm;
